import type { LucideIcon } from 'lucide-react'

import {
    Armchair,
    BadgeCheck,
    CalendarCheck,
    Droplets,
    Hand,
    Receipt,
    ShieldCheck,
    Sparkles,
    SprayCan,
    Timer,
    Users
} from 'lucide-react'

type IconItem = {
    icon: LucideIcon
    title: string
    body: string
}

type Testimonial = {
    quote: string
    name: string
    vehicle: string
}

type Faq = {
    question: string
    answer: string
}

export const HIGHLIGHTS: Array<IconItem> = [
    {
        icon: Droplets,
        title: "Snow foam pre-wash",
        body: "Thick foam lifts grit before a wash mitt ever touches your paint."
    },
    {
        icon: Hand,
        title: "Two-bucket hand wash",
        body: "Fresh, clean water for every panel, so dirt is never dragged around."
    },
    {
        icon: Armchair,
        title: "Interior valeting",
        body: "Vacuumed, wiped and freshened, right down to the door pockets."
    },
    {
        icon: Timer,
        title: "Ready while you wait",
        body: "Quick washes take minutes. Valets are done while you run errands."
    }
]

export const STEPS: Array<IconItem> = [
    {
        icon: CalendarCheck,
        title: "Book or drive in",
        body: "Reserve a slot online in under a minute, or simply pull into the bay."
    },
    {
        icon: SprayCan,
        title: "Foam and pre-rinse",
        body: "Snow foam softens the grime and a gentle rinse carries it safely away."
    },
    {
        icon: Hand,
        title: "Hand wash and finish",
        body: "Two-bucket washing, a soft microfibre dry, then the extras you picked."
    },
    {
        icon: BadgeCheck,
        title: "Check and drive off",
        body: "We walk round the car with you. Spot something? We fix it there and then."
    }
]

export const REASONS: Array<IconItem> = [
    {
        icon: ShieldCheck,
        title: "Paint-safe products",
        body: "pH-neutral shampoos and non-acidic wheel cleaners that are kind to wax, ceramic coatings and trim."
    },
    {
        icon: Sparkles,
        title: "Clean kit, every car",
        body: "Fresh microfibre mitts and towels each time, so yesterday's grime never meets your paintwork."
    },
    {
        icon: Receipt,
        title: "Upfront prices",
        body: "Clear prices for every package and add-on. If a bigger vehicle costs more, we tell you before we start."
    },
    {
        icon: Users,
        title: "People who care",
        body: "A friendly local team who give a family hatchback the same attention as a sports car."
    }
]

export const TESTIMONIALS: Array<Testimonial> = [
    {
        quote: "I came in for a quick wash and left with a car that looked better than the day I bought it. The interior valet got rid of two years of school runs.",
        name: "Hannah W.",
        vehicle: "Skoda Octavia Estate"
    },
    {
        quote: "Quick, friendly and properly thorough. A wash here has become my Saturday ritual, and the tyres always look brand new.",
        name: "Marcus T.",
        vehicle: "BMW 3 Series"
    },
    {
        quote: "Our dog sheds like mad. They got every hair out of the boot and the seats, and the car smells fresh again. Worth every penny.",
        name: "Priya S.",
        vehicle: "Volvo XC60"
    }
]

export const FAQS: Array<Faq> = [
    {
        question: "Do I need to book, or can I just turn up?",
        answer: "Drive-ins are always welcome. Booking online simply means the bay is ready when you arrive, which is handy for valets and details that take longer."
    },
    {
        question: "How long will my car take?",
        answer: "It depends on the package. Each one on our price list shows a typical time, from a quick exterior wash to a full detail, and we keep your slot clear for it."
    },
    {
        question: "Will a hand wash scratch my paint?",
        answer: "Not the way we do it. Snow foam lifts grit first, we use the two-bucket method, and every car gets clean microfibre mitts and towels, so dirt is never rubbed into your paintwork."
    },
    {
        question: "Can you get rid of pet hair and stains?",
        answer: "Yes. Add pet hair removal or an odour treatment to any wash, and our deeper valets shampoo seats and carpets to lift stubborn marks."
    },
    {
        question: "How do I pay?",
        answer: "You pay when you collect your car, by card, contactless or cash. Booking online is free and there's nothing to pay up front."
    },
    {
        question: "What if I'm not happy with the result?",
        answer: "Tell us before you drive away and we'll put it right there and then, free of charge."
    }
]

export const PROMISE = {
    title: "Our clean-car promise",
    body: "Not happy with any part of your clean? Tell us before you drive away and we'll put it right, free of charge."
}
